/**
 * User Profile Management Service
 * 
 * Handles API calls for user profile updates and account management
 */

import { api } from './api'
import { logger } from '../utils/logger'

export interface UpdateProfileRequest {
  full_name: string
  email: string
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export interface UpdateProfileResponse {
  id: string
  email: string
  full_name: string
  is_active: boolean
  role?: string
  user_type?: string
  created_at: string
  updated_at: string
}

/**
 * User Profile Service Class
 */
class UserService {

  /**
   * Update user profile information
   */
  async updateProfile(profileData: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    try {
      logger.debug('Updating user profile', {
        service: 'UserService',
        action: 'update_profile',
        email: profileData.email
      })

      const response = await api.put('/users/profile', profileData)
      
      logger.info('Profile updated successfully', {
        service: 'UserService',
        action: 'profile_updated',
        userId: response.data.id
      })

      return response.data
    } catch (error) {
      logger.error('Failed to update profile', {
        service: 'UserService',
        action: 'update_profile_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to update profile')
    }
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    try {
      logger.debug('Changing user password', {
        service: 'UserService',
        action: 'change_password'
      })

      await api.put('/users/password', passwordData)
      
      logger.info('Password changed successfully', {
        service: 'UserService',
        action: 'password_changed'
      })
    } catch (error) {
      logger.error('Failed to change password', {
        service: 'UserService',
        action: 'change_password_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to change password')
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentProfile(): Promise<UpdateProfileResponse> {
    try {
      logger.debug('Fetching current user profile', {
        service: 'UserService',
        action: 'get_current_profile'
      })

      const response = await api.get('/users/me')
      
      logger.debug('Current profile fetched successfully', {
        service: 'UserService',
        action: 'current_profile_fetched',
        userId: response.data.id
      })

      return response.data
    } catch (error) {
      logger.error('Failed to fetch current profile', {
        service: 'UserService',
        action: 'get_current_profile_error',
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw new Error('Failed to fetch current profile')
    }
  }
}

// Create and export service instance
export const userService = new UserService()

// Export service class for testing
export { UserService }